import type { Question } from '@new_project/shared';

type Answers = Record<string, unknown>;

/**
 * Answers the wizard pre-fills at creation, whatever the template's own default.
 *
 * `deploy` is checked at creation (the deploy spec), while the templates keep
 * `default: false` on purpose: a `copier update --defaults` falls back on the
 * template's default for a question it must answer on its own, so a `true`
 * there would turn the deploy chain on in every project that has it off. Only
 * the wizard pre-checks it, and only when the template asks a bool `deploy`
 * question. The `/questions` endpoint keeps showing copier.yml as it is.
 */
export const CREATION_DEFAULTS: Readonly<Record<string, boolean>> = { deploy: true };

/** Literal (non-Jinja) defaults can be pre-filled; Jinja defaults are left to copier. */
function isLiteralDefault(d: unknown): boolean {
  if (typeof d === 'string') return !/\{\{|\{%/.test(d);
  return d !== undefined;
}

/** The initial answers of a template's form: its literal defaults, then the creation overrides. */
export function seedAnswers(questions: Question[]): Answers {
  const seeded: Answers = {};
  for (const q of questions) {
    const override = CREATION_DEFAULTS[q.name];
    if (q.type === 'bool' && override !== undefined) seeded[q.name] = override;
    else if (isLiteralDefault(q.default)) seeded[q.name] = q.default;
  }
  return seeded;
}
